import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { type CheckEdit, foldIdentifier, namesModel } from '@rock/recalls';
import {
    type MergedRecallCheck,
    type MergedRecallModelRule,
    type RecallCheckKind,
    RecallCheckKindSchema,
} from '@rock/types';
import { Pencil, X } from 'lucide-react';
import { type FC, useId, useState } from 'react';
import { SourceTags } from './recallTags';

export const CHECK_KIND_LABELS: Record<RecallCheckKind, string> = {
    serial: 'Serial number',
    batch: 'Batch or lot',
    date: 'Manufacture date',
    upc: 'UPC',
    other: 'Other',
};

/** Each kind as the rules summary says it: "Homeowner checks the serial number and the UPC." */
export const CHECK_KIND_PHRASES: Record<RecallCheckKind, string> = {
    serial: 'serial number',
    batch: 'batch or lot number',
    date: 'manufacture date',
    upc: 'UPC',
    other: 'other details',
};

/**
 * One check as the homeowner reads it, with its kind, the model numbers it binds and who supplied it, and
 * Edit and Remove (locked while another check's form is open). A check scoped only to numbers no model rule
 * names binds every model, which the row says in amber.
 */
export const CheckRow: FC<{
    check: MergedRecallCheck;
    modelRules: MergedRecallModelRule[];
    locked: boolean;
    onEdit: () => void;
    onRemove: () => void;
}> = ({ check, modelRules, locked, onEdit, onRemove }) => (
    <div className="flex flex-col gap-1 rounded border px-3 py-2">
        <div className="flex items-center gap-2">
            <span className="text-xs font-semibold">{CHECK_KIND_LABELS[check.kind]}</span>
            <SourceTags source={check.source} />
            <span className="text-xs text-muted-foreground">
                {check.appliesToModels.length ? check.appliesToModels.join(', ') : 'All model numbers'}
            </span>
            <span className="ml-auto flex items-center gap-1">
                <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    disabled={locked}
                    onClick={onEdit}
                    aria-label="Edit check"
                >
                    <Pencil className="h-3 w-3" />
                </Button>
                <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    disabled={locked}
                    onClick={onRemove}
                    aria-label="Remove check"
                >
                    <X className="h-3 w-3" />
                </Button>
            </span>
        </div>
        <p className="m-0">{check.text}</p>
        {namesNoModel(check, modelRules) && (
            <p className="m-0 text-xs text-amber-800">
                No model rule names {check.appliesToModels.join(', ')}, so this check binds every model.
            </p>
        )}
    </div>
);

/**
 * Add or edit one check: what the homeowner looks at, in the notice's words, and the model numbers it binds
 * (every listed model, or the checked ones). Numbers it was scoped to that are not a rule's value are kept,
 * listed below the checkboxes.
 */
export const CheckForm: FC<{
    initial?: MergedRecallCheck;
    modelRules: MergedRecallModelRule[];
    onSave: (edit: CheckEdit) => Promise<void>;
    onCancel: () => void;
}> = ({ initial, modelRules, onSave, onCancel }) => {
    const formId = useId();
    const scopableRules = modelRules.filter((rule) => rule.kind !== 'any');
    const [kind, setKind] = useState<RecallCheckKind>(initial?.kind ?? 'other');
    const [text, setText] = useState(initial?.text ?? '');
    const [allModels, setAllModels] = useState(!initial?.appliesToModels.length);
    const [scoped, setScoped] = useState(new Set(initial?.appliesToModels.map(foldIdentifier)));
    const [saving, setSaving] = useState(false);
    const ruleFolds = new Set(scopableRules.map((rule) => foldIdentifier(rule.value)));
    const orphans = (initial?.appliesToModels ?? []).filter((value) => !ruleFolds.has(foldIdentifier(value)));
    const checked = scopableRules.filter((rule) => scoped.has(foldIdentifier(rule.value)));
    const edit: CheckEdit = {
        kind,
        text: text.trim(),
        appliesToModels: allModels ? [] : [...checked.map((rule) => rule.value), ...orphans],
    };
    const problem = !edit.text
        ? 'Say what the homeowner checks, in the notice’s words.'
        : !allModels && edit.appliesToModels.length === 0
          ? 'Check the model numbers this check binds, or choose All model numbers.'
          : undefined;
    const toggle = (fold: string) => {
        const next = new Set(scoped);
        if (next.has(fold)) next.delete(fold);
        else next.add(fold);
        setScoped(next);
    };
    const save = async () => {
        setSaving(true);
        await onSave(edit);
        setSaving(false);
    };
    return (
        <form
            className="flex flex-col gap-2 rounded border border-dashed px-3 py-2"
            onSubmit={(event) => {
                event.preventDefault();
                if (!problem && !saving) void save();
            }}
        >
            <Select value={kind} onValueChange={(value) => setKind(RecallCheckKindSchema.parse(value))}>
                <SelectTrigger className="h-8 w-48 text-xs">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {RecallCheckKindSchema.options.map((option) => (
                        <SelectItem key={option} value={option}>
                            {CHECK_KIND_LABELS[option]}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Textarea
                aria-label="What the homeowner checks"
                className="text-xs"
                placeholder="Batch numbers 1535 through 1545"
                value={text}
                onChange={(event) => setText(event.target.value)}
            />
            <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
                <legend className="text-xs text-muted-foreground">
                    Applies to {allModels ? 'all model numbers' : `${checked.length + orphans.length} model number(s)`}
                </legend>
                <span className="flex items-center gap-1 text-xs">
                    <Checkbox
                        id={`${formId}-all`}
                        checked={allModels}
                        onCheckedChange={(next) => setAllModels(next === true)}
                    />
                    <label htmlFor={`${formId}-all`}>All model numbers</label>
                </span>
                {!allModels && (
                    <div className="flex max-h-40 flex-wrap gap-x-3 gap-y-1 overflow-y-auto">
                        {scopableRules.map((rule) => {
                            const fold = foldIdentifier(rule.value);
                            return (
                                <span key={rule.value} className="flex items-center gap-1 font-mono text-xs">
                                    <Checkbox
                                        id={`${formId}-${fold}`}
                                        checked={scoped.has(fold)}
                                        onCheckedChange={() => toggle(fold)}
                                    />
                                    <label htmlFor={`${formId}-${fold}`}>{rule.value}</label>
                                </span>
                            );
                        })}
                    </div>
                )}
                {!allModels && orphans.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                        Also kept, not a rule above: <span className="font-mono">{orphans.join(', ')}</span>
                    </span>
                )}
            </fieldset>
            {problem && <p className="m-0 text-xs text-amber-800">{problem}</p>}
            <div className="flex gap-2">
                <Button size="sm" type="submit" disabled={!!problem || saving}>
                    Save check
                </Button>
                <Button size="sm" variant="outline" type="button" onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        </form>
    );
};

function namesNoModel(check: MergedRecallCheck, modelRules: MergedRecallModelRule[]): boolean {
    if (check.appliesToModels.length === 0) return false;
    return !check.appliesToModels.some((model) => namesModel(model, modelRules));
}
